<template>
  <div id="evolution-caracteristics">

    <h3>
      <small>
        <span v-html="fieldInfo.title"></span>
        <span v-if="fieldInfo.subtitle">
          - <span v-html="fieldInfo.subtitle"></span>
        </span>
      </small>
      <br>
      <span v-html="fieldInfo.field.t"></span>
      <br>

      <div class="btn-toolbar">
        <btn size="sm" @click="resetLinks">
          <span class="glyphicon glyphicon-refresh"
                aria-hidden="true">
          </span>
          Remettre à zéro
        </btn>
        <btn size="sm" @click="mergeAllColumns">
          <span class="glyphicon glyphicon-link"
                aria-hidden="true">
          </span>
          Une seule colonne
        </btn>
        <sui-checkbox v-model="displayAllFieldValues"
                      v-if="values && values.plotType === 'rep'"
                      type="bactADI" size="sm">
          Afficher toutes les valeurs possibles
        </sui-checkbox>
      </div>
    </h3>
    <table class="table table-responsive table-striped">
      <tbody>
        <tr>
          <th></th>
          <th v-for="(v, i) in linkedValues.columns" :key="`col-head-${i}`">
            {{ v.label }} <br>
            <dropdown tag="span">
              <btn class="dropdown-toggle" size="xs">
                <strong class="glyphicon glyphicon-link"
                        aria-hidden="true"></strong>
              </btn>
              <template slot="dropdown">
                <li v-for="(oc, j) in linkedValues.columns"
                    :key="`link-${i}-${j}`"
                    v-if="i !== j">
                  <a role="button" @click="setColumnLink(v.index, oc.index)">
                    {{ oc.key }}
                  </a>
                </li>
              </template>
            </dropdown>
            <btn size="xs" @click="showLabelModal(v.key)">
              <strong class="glyphicon glyphicon-pencil" aria-hidden="true">
              </strong>
            </btn>
            <btn size="xs" @click="hideColumn(v.index)">
              <strong class="glyphicon glyphicon-eye-close" aria-hidden="true">
              </strong>
            </btn>
          </th>
        </tr>
        <tr v-for="(r, i) in linkedValues.rows" :key="`row-${i}`"
            v-if="!r.noArray">
          <th>
            <span v-if="!r.noPlot && rowsLinkAvailable">
              <dropdown tag="span">
                <btn class="dropdown-toggle" size="xs">
                  <strong class="glyphicon glyphicon-link"
                          aria-hidden="true"></strong>
                </btn>
                <template slot="dropdown">
                  <li v-for="(or, j) in linkedValues.rows"
                      :key="`link-${i}-${j}`"
                      v-if="i !== j && !or.noPlot">
                    <a role="button" @click="setRowLink(r.index, or.index)">
                      {{ or.label }}
                    </a>
                  </li>
                </template>
              </dropdown>
              <btn size="xs" @click="showLabelModal(r.label)"
                   v-if="!r.noPlot">
                <strong class="glyphicon glyphicon-pencil" aria-hidden="true">
                </strong>
              </btn>
              <btn size="xs" @click="hideRow(r.index)" v-if="!r.noPlot">
                <strong class="glyphicon glyphicon-eye-close" aria-hidden="true">
                </strong>
              </btn>
            </span>
            {{ r.label }}
          </th>
          <td v-for="(c, j) in linkedValues.columns"
              :key="`row-${i}-col-${j}`">
            <span v-if="r.values[c.key] != null">
              <span v-if="r.values[c.key].label">
                {{ r.values[c.key].label }}
              </span>
              <span v-else>
                {{ displayValue(r.values[c.key]) }}
              </span>
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="testStat.result != null && testStat.result.p != null"
         :class="`test-stat-${testStat.detail ? 'detail' : 'summary' }`">
      <div v-if="!alwaysShowTestStatResult && !testStat.show">
        <btn type="warning" @click="testStat.show = true">
          Calculer le test statistique
        </btn>
      </div>
      <div v-if="alwaysShowTestStatResult || testStat.show">
        <h4>Résultat du test statistique «{{ testStatData.name }}»<br>
          <small>
            Calcul effectué avec R
          </small>
        </h4>
        <div class="test-stat-result">
          <p :class="`test-stat-result-${pValueStatus}`">
            <span class="conclusion">
              La
              <a href="http://fr.wikipedia.org/wiki/Valeur_p">
                valeur p (p-value)
              </a>
              de votre test est
              <u v-if="testStat.result.isExp">
                inférieure à {{this.pValueExponentialThreshold}}
              </u>
              <u v-else>{{testStat.result.p}}</u>.
            </span>
          </p>
          <div v-if="testStat.result.alert && isChiSquare">
            <p class="text-warning">
              Certaines cases des effectifs attendus
              sont inférieures à 5. Les conditions de validité du
              Chi2 ne sont pas remplies.
            </p>
          </div>
          <div>
            <btn @click="testStat.detail = !testStat.detail"
               size="xs">
              Detail
            </btn>
          </div>
          <collapse v-model="testStat.detail">
            <h4>Détails du test</h4>
            <ul class="res">
              <li v-if="testStat.result.isExp">
                <label>Valeur exacte de la P-value</label> :
                <span class="result">
                  {{testStat.result.p}}
                </span>
              </li>
              <li v-if="testStat.result.method">
                <label>M&eacute;thode</label> :
                <span class="result">
                  {{testStat.result.method}} {{testStat.result.alt}}
                </span>
              </li>
              <li v-if="testStat.result.qobs">
                <label>
                  Statistique observ&eacute;e Qobs
                </label> :
                <span class="result">{{testStat.result.qobs}}</span>
              </li>
              <li v-if="testStat.result.df">
                <label>
                  Param&egrave;tre du test / Degrés de liberté
                </label> :
                <span class="result">{{testStat.result.df}}</span>
              </li>
              <li v-if="testStat.result.ci">
                <label>
                  Intervalle de confiance à 95%
                </label> :
                <span class="result">{{testStat.result.ci}}</span>
              </li>
              <li v-if="isChiSquare"
                  v-for="(table, ii) in chi2Tables"
                  :key="`chi-col-header-${ii}`">
                <strong>{{table.title}}</strong>
                <br>
                <table class="table table-responsive table-striped table-fixed-first-row">
                  <tbody>
                    <tr>
                      <th></th>
                      <th  v-for="i in testStatData.cols"
                           :key="`chi-col-header-${i}`">
                        Y{{ i }}
                      </th>
                    </tr>
                    <tr v-for="(r, i) in table.val"
                        :key="`chi-row-${i}`">
                      <th>X{{ i + 1 }}</th>
                      <td v-for="(v, j) in r" :key="`chi-row-${i}-${j}`">
                        {{ v }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </li>
            </ul>
          </collapse>
        </div>
      </div>
    </div>

    <modal v-model="labelModal.show" title="Étiquettes"
           :append-to-body="true">
      <div class="form-group">
        <input id="src" tabindex="1" class="form-control"
               v-model.trim="labelModal.src" type="text"
               disabled placeholder="Texte à modifier"/>
      </div>
      <div class="form-group">
        <input id="target" tabindex="2" class="form-control"
               v-model.trim="labelModal.target" type="text"
               @keyup.enter="addLabel"
               placeholder="Nouveau texte"/>
      </div>
      <btn @click="addLabel"
           :disabled="labelModal.src === '' || labelModal.target ===''">
        Appliquer
      </btn>
      <hr>
      <h2>Liste des étiquettes</h2>
      <table class="table table-responsive">
        <tbody>
          <tr>
            <th>Source</th>
            <th>Nouveau texte</th>
          </tr>
          <tr v-for="(value, key) in labelModal.labels"
              :key="`label-${key}`">
            <td>
              <btn size="xs" @click="selectLabel(key, value)">
                <strong class="glyphicon glyphicon-pencil" aria-hidden="true">
                </strong>
              </btn>
              <btn size="xs" @click="removeLabel(key)">
                <strong class="glyphicon glyphicon-trash" aria-hidden="true">
                </strong>
              </btn>
              {{ key }}
            </td>
            <td>{{ value }}</td>
          </tr>
        </tbody>
      </table>
    </modal>
    <hr>
    <div class="icon-container">
      <a role="button"
         type="success" size="xl" v-for="(p, i) in charts"
         :class="{ 'selected': chart === i }"
         :key="`chart-${p.type}-${i}`"
         v-tooltip.unenterable="p.tooltip"
         @click="toggleChart(i)">
        <svg-icon :icon="p.icon" color="#fff"/>
      </a>
    </div>
    <hr>
    <div v-if="chart != null">
      <div class="panel panel-info" v-if="charts[chart].actions">
        <div class="panel-heading">
          <div class="action-bar">
            <div v-for="(t, i) in charts[chart].actions"
                 :key="`chart-actions-${i}`"
                 v-show="t.show !== false">
              <div v-if="t.type === 'checkbox'">
                <sui-checkbox :type="t.style"
                            size="sm"
                            v-model="actions[t.model]">
                  {{ t.label }}
                </sui-checkbox>
              </div>
              <div v-if="t.type === 'number'">
                <cleave v-model="actions[t.model]"
                        :options="numbersCleaveOptions"
                        class="form-control"
                        :placeholder="t.label"
                        autocomplete="off"
                        name="numeric">
                </cleave>
              </div>
              <div v-else-if="t.type === 'dropdown'">
                <dropdown>
                  <btn :type="t.style" class="dropdown-toggle">
                    {{ actions[t.model].l }}  <span class="caret"></span>
                  </btn>
                  <template slot="dropdown">
                    <li v-for="(r, k) in t.choices"
                        :key="`chart-actions-${i}-dd-${k}`">
                      <a role="button" @click="actions[t.model] = r">
                        {{ r.l }}
                      </a>
                    </li>
                  </template>
                </dropdown>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="chart-container" v-if="chartSize"
           :style="{ 'max-width': '100%', 'width': `${chartSize.width}px` }">
        <chart
          :initialRatio="chartSize.ratio"
          :nested="true"
          :initialWidth="chartSize.width"
          :key="`chart-${chart}-${charts[chart].refresh}`"
          :type="`${charts[chart].type}-chart`"
          :config="charts[chart].config"
          :plugins="charts[chart].plugins"
          :exportConfig="charts[chart].exportConfig">
        </chart>
      </div>
    </div>
  </div>
</template>
<script>
import svgIcon from '@/sui/components/svgIcon'
import chart from '@/sui/components/charts/chart'
import doughnut from '@/sui/tools/chart/doughnut'
import polar from '@/sui/tools/chart/polar'
import bar from '@/sui/tools/chart/bar'
import suiCheckbox from '@/sui/components/checkbox'
import go from '@/gojs/go'
import math from '@/sui/tools/math'
import assert from '@/sui/tools/assert'
import ctool from 'color'
import external from '@/sui/api/external'
import results from '@/sui/data/results'
import options from '@/sui/tools/chart/options'
import {
  incidenceBoxPlot,
  Labels,
  limitText,
  repColors
} from '@/sui/tools/chart/boxPlot'

import clone from 'clone'
import cleave from 'vue-cleave-component'
import rtti from '@/sui/tools/rtti'
import merge from '@/sui/tools/merge'

function buildPercentToggle (model) {
  return {
    type: 'checkbox',
    style: 'bactADI',
    model: model,
    label: 'Pourcentages'
  }
}

const config = {
  text: {
    addValue: function (result, cf, field, data) {
      let total = 0
      for (let key in data.values) {
        const count = data.values[key]
        if (count == null) {
          continue
        }

        let label = `«${key}»`
        if (field.v != null) {
          const entry = field.v.find(elt => `${elt.code}` === key)
          if (entry) {
            label = entry.value
          }
        }

        const f = {
          value: label
        }
        assert(f.value !== 'Effectif', `«Effectif» name is reserved.`)

        if (result[f.value] == null) {
          result[f.value] = {}
        }

        if (`${field.n}` !== key) {
          total += count
          result[f.value][cf] = {
            count: count
          }
        } else {
          result[f.value][cf] = count
        }
      }

      result['Effectif'][cf] = total
    },
    initialize: function (result) {
    },
    fillRows: function (result, field, rowKeys) {
      const rows = []
      const fields = []
      const keys = Array.from(rowKeys)
      for (let i = 0; i !== keys.length; ++i) {
        const key = keys[i]

        let label = `«${key}»`
        if (field.v != null) {
          const entry = field.v.find(elt => `${elt.code}` === key)
          if (entry) {
            label = entry.value
          }
        }

        fields.push(
          {
            value: label,
            code: key
          }
        )
      }

      const colors = repColors(fields)
      for (let i = 0; i !== fields.length; ++i) {
        const f = fields[i]
        const r = result[f.value]
        if (r == null) {
          if (rowKeys.has(`${f.code}`)) {
            rows.push(null)
          }
          continue
        }

        rows.push({
          label: f.value,
          values: r,
          color: colors[i],
          noPlot: (f.index != null ? f.index : f.code) === field.n
        })
      }

      return rows
    },

    mergeData: function (list) {
      let total = 0
      const newLine = {}

      for (let i = 0; i < list.length; i++) {
        const d = list[i]

        total += d.total

        const val = d.values
        for (const key of Object.keys(val)) {
          const v = val[key]

          const existing = newLine[key]
          newLine[key] = (existing == null ? 0 : existing) + v
        }
      }
      return {
        pop: {
          total: total,
          type: 'text',
          values: newLine
        },
        stat: null
      }
    }
  },

  rep: {
    addValue: function (result, cf, field, data) {
      let total = 0
      for (let key in data.values) {
        const count = data.values[key]
        if (count == null) {
          continue
        }

        let f = field.v.find(
          l => `${(l.index != null ? l.index : l.code)}` === key
        )
        assert(f.value !== 'Effectif', `«Effectif» name is reserved.`)

        if (f == null) {
          f = {
            value: `«${key}»`
          }
        }

        if (result[f.value] == null) {
          result[f.value] = {}
        }

        if (`${field.n}` !== key) {
          total += count
          result[f.value][cf] = {
            count: count
          }
        } else {
          result[f.value][cf] = count
        }
      }

      result['Effectif'][cf] = total
    },
    initialize: function (result) {
    },

    fillRows: function (result, field, rowKeys) {
      const rows = []
      const fields = []
      for (let i = 0; i !== field.v.length; ++i) {
        const f = field.v[i]
        fields.push(f)
      }

      for (let k in result) {
        if (!k.startsWith('«')) {
          continue
        }
        fields.push(
          {
            value: k,
            code: k
          }
        )
      }

      const colors = repColors(fields)
      for (let i = 0; i !== fields.length; ++i) {
        const f = fields[i]
        const r = result[f.value]
        if (r == null) {
          if (rowKeys.has(`${f.code}`)) {
            rows.push(null)
          }
          continue
        }

        rows.push({
          label: f.value,
          values: r,
          color: colors[i],
          noPlot: (f.index != null ? f.index : f.code) === field.n
        })
      }

      return rows
    },

    mergeData: function (list) {
      let total = 0
      const newLine = {}

      for (let i = 0; i < list.length; i++) {
        const d = list[i]

        total += d.total

        const val = d.values
        for (const key of Object.keys(val)) {
          const v = val[key]

          const existing = newLine[key]
          newLine[key] = (existing == null ? 0 : existing) + v
        }
      }
      return {
        pop: {
          total: total,
          type: 'rep',
          values: newLine
        },
        stat: null
      }
    }
  },

  mm: {
    addValue: function (result, cf, field, data, stats) {
      result['Moyenne'][cf] = stats.mean
      result['Médiane'][cf] = stats.median
      result['Borne outlier haute'][cf] = stats.sup
      result['Borne outlier basse'][cf] = stats.inf
      if (data.num != null) {
        result['Numérateur total'][cf] = data.num
      }
      if (data.div != null) {
        result['Diviseur total'][cf] = data.div
      }
      if (data.meanp != null) {
        result['Moyenne pondérée'][cf] = data.meanp
      }
      result['Effectif'][cf] = { values: data.values, label: data.total }
    },

    initialize: function (result) {
      result['Moyenne'] = {}
      result['Médiane'] = {}
      result['Borne outlier haute'] = {}
      result['Borne outlier basse'] = {}
      result['Numérateur total'] = {}
      result['Diviseur total'] = {}
      result['Moyenne pondérée'] = {}
    },

    fillRows: function (result, field) {
      const rows = [
        { label: 'Moyenne', values: result['Moyenne'] },
        { label: 'Médiane', values: result['Médiane'] },
        { label: 'Borne outlier haute', values: result['Borne outlier haute'] },
        { label: 'Borne outlier basse', values: result['Borne outlier basse'] }
      ]

      const num = result['Numérateur total']
      if (Object.keys(num).length > 0) {
        rows.push({ label: 'Numérateur total', values: num })
      }

      const div = result['Diviseur total']
      if (Object.keys(div).length > 0) {
        rows.push({ label: 'Diviseur total', values: div })
      }

      const meanp = result['Moyenne pondérée']
      if (Object.keys(meanp).length > 0) {
        rows.push({ label: 'Moyenne pondérée', values: meanp })
      }
      return rows
    },

    mergeData: function (list) {
      assert(list.length > 0)

      const first = list[0]
      if (list.length === 1) {
        return {
          pop: first,
          stat: math.statistics(first.values)
        }
      }

      const finalPop = { sum: 0, total: 0, type: 'mm', values: [] }
      const hasMeanP = first.meanp != null &&
          first.num != null &&
          first.div != null &&
          first.factor != null
      const firstFactor = hasMeanP ? first.factor : null

      let sumNum = 0
      let sumDiv = 0
      for (let i = 0; i < list.length; i++) {
        const d = list[i]

        if (hasMeanP) {
          const { num, div, meanp, factor } = d
          assert(num != null && div != null && meanp != null && factor != null)
          assert.equals(firstFactor, factor)
          sumNum += num
          sumDiv += div
        }
        finalPop.sum += d.sum
        finalPop.total += d.total
        finalPop.values.push(...d.values)
      }

      if (hasMeanP) {
        finalPop.num = sumNum
        finalPop.div = sumDiv
        finalPop.meanp = (sumNum / sumDiv) * firstFactor
      }

      return {
        pop: finalPop,
        stat: math.statistics(finalPop.values)
      }
    }
  }
}

function addValues (f, s) {
  if (!rtti.isObject(f)) {
    return f + s
  }

  return {
    count: f.count + s.count
  }
}

function addRows (f, s) {
  f.label = `${f.label} + ${s.label}`
  for (let k in f.values) {
    const up = s.values[k]
    if (up == null) {
      continue
    }

    f.values[k] = addValues(f.values[k], up)
  }

  for (let k in s.values) {
    if (f.values[k] == null) {
      f.values[k] = s.values[k]
    }
  }
}

function getIndex (normalIdx, cl) {
  let i = normalIdx
  while (i < cl.length && cl[i].length === 0) {
    i++
  }
  return i
}

function makeTable (data, field, cfg, cl, colLabels, rowKeys) {
  const columns = []
  const values = {
    'Effectif': {}
  }

  cfg.initialize(values)

  let idx = getIndex(0, cl)
  for (let i = 0; i < colLabels.length; i++) {
    const key = colLabels[i]
    const dat = data[key]

    const v = dat.pop
    const stat = dat.stat

    if (v.total === 0) {
      continue
    }

    columns.push({ key: key, label: key, index: idx })
    cfg.addValue(values, key, field, v, stat)
    idx = getIndex(idx + 1, cl)
  }

  let rows = cfg.fillRows(values, field, rowKeys)
  rows.push(
    {
      label: 'Effectif',
      values: values['Effectif'],
      noPlot: true
    }
  )
  return { columns: columns, rows: rows }
}

function getFieldValueNumericCodeOrIndex (base) {
  return base.index != null ? base.index : base.code
}

function initializeDataSource (base, source, forceDisplay) {
  if (source.type !== 'rep') {
    return source
  }

  const sourceValues = source.values
  const resultValues = {}

  let hasValues = false
  for (let i = 0; i !== base.length; i++) {
    const baseCode = getFieldValueNumericCodeOrIndex(base[i])
    const sourceValue = sourceValues[baseCode]

    if (sourceValue == null) {
      if (forceDisplay) {
        resultValues[baseCode] = 0
      }
      continue
    }
    hasValues = true
    resultValues[baseCode] = sourceValue
  }

  if (!hasValues) {
    return null
  }

  return {
    values: resultValues,
    total: source.total,
    type: source.type
  }
}

export default {
  name: 'evolution-caracteristics',

  props: {
    sources: {
      type: Array,
      required: true
    },

    fieldInfo: {
      type: Object,
      required: true
    },

    columnField: {
      type: String,
      default: 'year'
    },

    alwaysShowTestStatResult: {
      type: Boolean,
      default: true
    }
  },

  components: {
    'chart': chart,
    'cleave': cleave,
    'sui-checkbox': suiCheckbox,
    'svg-icon': svgIcon
  },

  data: function () {
    return {
      displayAllFieldValues: false,
      pValueExponentialThreshold: 0.0001,
      links: {
        rows: [],
        columns: []
      },
      plotKey: null,
      chart: null,
      labelModal: {
        show: false,
        src: '',
        target: '',
        labels: {}
      },

      testStat: {
        result: null,
        detail: false,
        show: false
      },

      actions: {
        percent: true,
        splitPercent: false,
        doughnutPercent: true,
        polarPercent: false,

        curve: true,
        limit: true,
        value: true,
        points: true,
        boxplot: true,
        median: true,
        outliers: true,
        mean: false,
        meanp: false,
        indicator: false,
        limitValue: ''
      },

      numbersCleaveOptions: {
        numeral: true,
        numeralDecimalScale: 6
      }
    }
  },

  watch: {
    sources: function () {
      this.resetLinks()
    },

    fieldInfo: function () {
      this.resetLinks()
      this.chart = null
      this.testStat.result = null
      this.testStat.show = false
      this.labelModal.labels = {}
    },

    displayAllFieldValues: function () {
      this.resetRowsLinks()
    },

    linkedValues: function () {
      this.testStat.result = null
      this.testStat.show = false
    },

    testStatData: {
      handler: function (v) {
        if (v != null && v.err != null) {
          this.noTestNotify(v.err)
          return
        }

        const vm = this
        vm.testStat.result = null
        vm.testStat.show = false
        this.$nextTick(() => vm.computeTestStat())
      },
      deep: true
    },

    charts: function (n, o) {
      const idx = this.chart
      if (idx == null || idx < 0 || idx >= o.length) {
        return
      }

      const id = o[idx].id
      const nidx = n.findIndex(p => p.id === id)
      if (nidx !== -1) {
        this.chart = nidx
      } else {
        this.chart = null
      }
    }
  },

  computed: {
    displayAllFieldValuesDefault: function () {
      return !!this.fieldInfo.displayAllValues
    },

    isChiSquare: function () {
      return this.testStatData.type === 'chisquare'
    },

    chi2Tables: function () {
      return {
        t1: {
          title: "Valeurs extraites du tableau d'évolution : ",
          val: this.testStatData.request.values
        },
        t2: {
          title: 'Tableau des effectifs attendus sous H0 : ',
          val: this.testStat.result.exp
        }
      }
    },

    rowsLinkAvailable: function () {
      return this.values != null &&
             (this.values.plotType === 'rep' || this.values.plotType === 'text')
    },

    getLimit: function () {
      if (!this.actions.indicator) {
        return null
      }

      if (this.actions.limitValue !== '') {
        return parseFloat(this.actions.limitValue)
      }

      const dot = this.fieldInfo.field.f.indexOf('.')

      if (dot === -1) {
        return null
      }
      const key = this.fieldInfo.field.f.slice(dot + 1)

      return results.getIndicatorLimitFromKey(key)
    },

    testStatData: function () {
      const t = this.linkedValues.type
      if (t == null || t === 'text') {
        return null
      }

      if (t === 'rep') {
        return this.repTestData
      }

      assert.equals(t, 'mm', 'Unknown caracteristics data type')
      return this.quantitativeTestData
    },

    quantitativeTestData: function () {
      const lv = this.linkedValues
      const cols = lv.columns
      const values = lv.rows[lv.rows.length - 1].values
      const request = []

      for (let i = 0; i !== cols.length; ++i) {
        const year = cols[i].key
        const target = []
        const data = values[year].values
        for (let j = 0; j !== data.length; ++j) {
          target.push(data[j])
        }
        request.push(target)
      }

      const { type, name, err } = math.getTestStat.mm(request)

      const finalRequest =
          type === 'mann' || type === 'student'
            ? { first: request[0], second: request[1] } : { values: request }

      return {
        type: type,
        name: name,
        request: finalRequest,
        err: err
      }
    },

    repTestData: function () {
      const lv = this.linkedValues
      const request = []
      for (let i = 0; i !== lv.rows.length; ++i) {
        const r = lv.rows[i]
        if (r.noPlot) {
          continue
        }

        const row = []
        for (let j = 0; j !== lv.columns.length; ++j) {
          const c = lv.columns[j]
          const v = r.values[c.key]
          row.push(
            v == null ? 0 : v.count
          )
        }
        request.push(row)
      }

      const { type, name, err } = math.getTestStat.rep(request)
      return {
        type: type,
        name: name,
        request: { values: request },
        err: err
      }
    },

    pValueStatus: function () {
      if (this.testStat.result == null) {
        return null
      }

      const p = this.testStat.result.p
      if (p == null) {
        return null
      }

      return p <= 0.05 ? 'ok' : 'nok'
    },

    selectedSourceValues: function () {
      const source = this.sources
      const sourceLen = source.length
      if (sourceLen === 0) {
        return null
      }

      const selectedField = this.fieldInfo.field.f

      const result = []
      for (let i = 0; i !== sourceLen; ++i) {
        const dataset = source[i]
        assert.notNull(dataset)
        const dataSource = dataset.dist[selectedField]
        if (dataSource == null) {
          continue
        }

        const dist = initializeDataSource(
          this.fieldInfo.field.v, dataSource, this.displayAllFieldValues
        )
        if (dist == null) {
          continue
        }
        result.push(
          {
            dset: dataset.dset,
            year: dataset.year,
            dist: dist
          }
        )
      }

      return result
    },

    values: function () {
      const selectedSourceValues = this.selectedSourceValues
      if (selectedSourceValues == null || selectedSourceValues.length === 0) {
        return null
      }

      let data = {}
      let columns = []
      const field = this.fieldInfo.field
      let plotType = null
      const rows = new Set()

      for (let i = 0; i !== selectedSourceValues.length; ++i) {
        const s = selectedSourceValues[i]
        const v = s.dist

        if (v == null || v.total === 0) {
          continue
        }

        if (plotType == null) {
          plotType = v.type
        } else {
          assert(plotType === v.type)
        }

        const key = `${s.year}`
        data[key] = v
        columns.push({ key: key, label: key })

        for (let key in v.values) {
          rows.add(key)
        }
      }

      return {
        columns: columns,
        data: data,
        field: field,
        plotType: plotType,
        rowKeys: rows
      }
    },

    linksReady: function () {
      return this.links.columns.length !== 0 &&
             (!this.rowsLinkAvailable || this.links.rows.length !== 0)
    },

    linkedValues: function () {
      if (!this.linksReady) {
        return { type: null }
      }

      const { columns, data, field, plotType, rowKeys } = this.values
      const cfg = config[plotType]
      let newData = {}
      let newDataList = []

      const columnLinks = this.links.columns

      for (let i = 0; i !== columnLinks.length; ++i) {
        const lc = columnLinks[i]
        if (lc.length === 0) {
          continue
        }

        let nc = null
        let colData = []
        for (let j = 0; j !== lc.length; ++j) {
          const index = lc[j]
          const c = columns[index].key
          if (nc == null) {
            nc = {
              index: index,
              key: clone(c)
            }
            colData.push(data[c])
          } else {
            nc.key = `${nc.key} + ${c}`
            colData.push(data[c])
          }
        }

        if (colData.length > 0) {
          newDataList.push(nc.key)
          newData[nc.key] = cfg.mergeData(colData)
        }
      }

      const table = makeTable(newData, field, cfg, columnLinks, newDataList, rowKeys)
      const rows = table.rows
      const newColumns = table.columns
      for (let i = 0; i !== newColumns.length; ++i) {
        newColumns[i].label = this.applyLabels(newColumns[i].key)
      }

      if (plotType === 'mm') {
        return {
          data: newData,
          columns: newColumns,
          rows: rows,
          type: plotType
        }
      }

      assert.equals(rows.length - 1, this.links.rows.length)

      const lrows = []
      for (let i = 0; i !== this.links.rows.length; ++i) {
        const lr = this.links.rows[i]
        if (lr.length === 0) {
          continue
        }

        let nr = null
        for (let j = 0; j !== lr.length; ++j) {
          const index = lr[j]
          const r = rows[index]
          if (r == null) {
            continue
          }

          if (nr == null) {
            nr = clone(r)
            nr.index = index
            lrows.push(nr)
          } else {
            addRows(nr, r)
          }
        }
      }

      const counts = {}
      for (let i = 0; i !== newColumns.length; ++i) {
        const key = newColumns[i].key

        let total = 0
        const update = []
        for (let j = 0; j !== lrows.length; ++j) {
          const r = lrows[j]
          if (r.noPlot) {
            continue
          }

          const v = r.values[key]
          if (v == null) {
            continue
          }

          total += v.count
          update.push(v)
        }
        counts[key] = total

        for (let j = 0; j !== update.length; ++j) {
          const v = update[j]
          v.total = total

          if (v.count === 0 && v.total === 0) {
            v.label = '0'
            continue
          }

          const percent = (100 * v.count / total).toFixed(1)
          v.label = `${v.count} (${percent}%)`
        }
      }

      for (let i = 0; i !== lrows.length; ++i) {
        lrows[i].label = this.applyLabels(lrows[i].label)
      }

      lrows.push({
        label: 'Effectif',
        values: counts,
        noPlot: true
      })

      return {
        data: newData,
        columns: newColumns,
        rows: lrows,
        type: plotType
      }
    },

    repDoughnutData: function () {
      const lv = this.linkedValues
      if (lv == null || lv.columns.length !== 1) {
        return null
      }

      const labels = []
      const data = []
      const colors = []
      for (let i = 0; i !== lv.rows.length; ++i) {
        const c = lv.rows[i]
        if (c.noPlot) {
          continue
        }

        labels.push(limitText(c.label))
        colors.push(c.color)
        const l = lv.columns[0].key
        const cv = c.values[l]
        if (cv != null) {
          data.push(cv.count)
        } else {
          data.push(null)
        }
      }

      return {
        labels: labels,
        title: `${this.fieldInfo.field.t} (${lv.columns[0].label})`,
        dataset: [
          {
            fill: true,
            backgroundColor: colors,
            data: data
          }
        ]
      }
    },

    repChartData: function () {
      const lv = this.linkedValues
      if (lv == null) {
        return null
      }

      const datasets = []
      for (let i = 0; i !== lv.rows.length; ++i) {
        const c = lv.rows[i]
        if (c.noPlot) {
          continue
        }

        const dataset = {
          label: limitText(c.label),
          backgroundColor: c.color,
          data: []
        }

        for (let j = 0; j !== lv.columns.length; ++j) {
          const l = lv.columns[j].key
          const cv = c.values[l]
          if (cv != null) {
            dataset.data.push(cv.count)
          } else {
            dataset.data.push(null)
          }
        }
        datasets.push(dataset)
      }

      const labels = []
      for (let i = 0; i !== lv.columns.length; ++i) {
        const col = lv.columns[i]
        const total = lv.rows[lv.rows.length - 1].values[col.key]
        const label = col.label
        labels.push(`${label} (${total})`)
      }

      return {
        title: this.fieldInfo.field.t,
        labels: labels,
        datasets: datasets
      }
    },

    isYesNoCharacteristics: function () {
      const lv = this.linkedValues
      const rows = lv.rows
      if (lv.type !== 'rep' || rows.length !== 3) {
        return false
      }

      const expected = go.fields.yesNo
      return rows[0].label === expected[0].value &&
             rows[1].label === expected[1].value
    },

    charts: function () {
      const lv = this.linkedValues
      if (this.sources.length === 0 || lv.type == null) {
        return []
      }

      const repChartData = this.repChartData
      if ((lv.type === 'rep' || lv.type === 'text') && repChartData != null) {
        const { title, labels, datasets } = repChartData
        const togglePercent = buildPercentToggle('percent')
        const toggleSplitPercent = buildPercentToggle('splitPercent')
        const result = [
          merge(
            bar.classic(
              'rep', title, datasets, labels, {
                defaultFormatter: this.actions[togglePercent.model] ? 2 : 0
              }
            ),
            { actions: [ togglePercent ] }
          ),
          bar.classic(
            'rep-100', title, datasets, labels, {
              percentBars: true
            }
          ),
          merge(
            bar.classic(
              'rep-split', title, datasets, labels, {
                splitBars: true,
                defaultFormatter: this.actions[toggleSplitPercent.model] ? 2 : 0
              }
            ),
            { actions: [toggleSplitPercent] }
          ),
          bar.classic(
            'rep-split-100', title, datasets, labels, {
              percentBars: true,
              splitBars: true
            }
          )
        ]

        if (this.isYesNoCharacteristics) {
          result.push(
            merge(
              bar.indicator(
                'rep-indicator',
                title,
                datasets,
                labels
              ), {
                refresh: labels.length
              }
            )
          )
        }

        const repDoughnutData = this.repDoughnutData
        if (lv.columns.length === 1 && repDoughnutData != null) {
          const toggleDoughnutPercent = buildPercentToggle('doughnutPercent')
          const togglePolarPercent = buildPercentToggle('polarPercent')

          const { labels, title, dataset } = repDoughnutData
          result.push(
            merge(
              doughnut.classic(
                'rep-doug', title, dataset, labels, {
                  defaultFormatter: this.actions[toggleDoughnutPercent.model] ? 2 : 0
                }
              ),
              { actions: [toggleDoughnutPercent] }
            ),
            merge(
              polar.classic(
                'rep-polar', title, dataset, labels, {
                  defaultFormatter: this.actions[togglePolarPercent.model] ? 2 : 0
                }
              ),
              { actions: [togglePolarPercent] }
            )
          )
        }
        return result
      }

      return [this.mmChart('boxplot')]
    },

    chartSize: function () {
      if (this.charts == null || this.chart == null) {
        return null
      }

      const currentChart = this.charts[this.chart]

      if (currentChart.expectedRatio == null) {
        return { ratio: 1.2, width: 800 }
      }

      const width = 180 * currentChart.config.data.datasets[0].data.length
      return {
        ratio: currentChart.expectedRatio,
        width: width
      }
    }

  },

  methods: {
    resetDisplayAllFieldValuesButton: function () {
      this.displayAllFieldValues = this.displayAllFieldValuesDefault
    },

    noTestNotify: function (text) {
      if (this.alwaysShowTestStatResult || this.testStat.show) {
        this.$notify(
          {
            type: 'warning',
            title: 'Test statistique impossible',
            duration: 4000,
            content: text
          }
        )
      }
    },

    displayValue: function (value) {
      if (rtti.isFloat(value)) {
        return value.toFixed(3)
      }
      return value
    },

    computeTestStat: function () {
      const testStatData = this.testStatData
      if (testStatData == null || testStatData.err != null) {
        return
      }

      function firstValueOrNull (v) {
        if (v == null || v.length == null || v.length !== 1) {
          return null
        }
        return v[0]
      }

      function getPvalueAndFormat (v, t) {
        const p = parseFloat(firstValueOrNull(v))
        const exp = p <= t
        return {
          exp: exp, val: exp ? p.toExponential(4) : p.toFixed(5)
        }
      }

      const { request, type } = testStatData
      const vm = this

      external.testStat(request, type).then(
        r => {
          const testResult = r.data
          const isTimeout = firstValueOrNull(testResult.timeout)
          if (isTimeout) {
            this.noTestNotify('Durée de calcul supérieure à 45 secondes')
            return
          }

          if (testResult.err == null) {
            const alt = firstValueOrNull(testResult.alt)
            const formatedP = getPvalueAndFormat(
              testResult.pval,
              this.pValueExponentialThreshold
            )

            vm.testStat.result = {
              p: formatedP.val,
              isExp: formatedP.exp,
              qobs: firstValueOrNull(testResult.qobs),
              method: firstValueOrNull(testResult.meth),
              df: firstValueOrNull(testResult.df),
              ci: testResult.ci,
              alt: alt ? `; Alternative : ${alt}` : null,
              alert: firstValueOrNull(testResult.alert),
              exp: testResult.expv
            }
          } else {
            vm.$notify(
              {
                type: 'danger',
                title: 'Erreur !',
                duration: 8000,
                content: `Une erreur est survenue lors de la requête statistique R : ${r.err}.`
              }
            )
          }
        }
      ).catch(
        e => {
          vm.$notify(
            {
              type: 'danger',
              title: 'Erreur !',
              duration: 8000,
              content: `Une exception est survenue lors de la requête statistique R : ${e}.`
            }
          )
        }
      )
    },

    applyLabels: function (text) {
      let result = text
      const labels = this.labelModal.labels
      for (let key in labels) {
        result = result.replace(key, labels[key])
      }
      return result
    },

    showLabelModal: function (t) {
      this.selectLabel(t, this.labelModal.labels[t])
      this.labelModal.show = true
    },

    addLabel: function () {
      const { src, target } = this.labelModal
      if (src !== '' && target !== '') {
        this.$set(this.labelModal.labels, src, target)
        this.labelModal.src = ''
        this.labelModal.target = ''
      }
    },

    selectLabel: function (src, target) {
      this.labelModal.src = src
      this.labelModal.target = target == null ? '' : target
    },

    removeLabel: function (key) {
      this.$delete(this.labelModal.labels, key)
    },

    setLink: function (a, s, d) {
      a[d].push(...a[s])
      this.hide(a, s)
    },

    hide: function (a, s) {
      this.$set(a, s, [])
    },

    hideRow: function (i) {
      this.hide(this.links.rows, i)
    },

    hideColumn: function (i) {
      this.hide(this.links.columns, i)
    },

    setRowLink: function (f, s) {
      const dest = f < s ? f : s
      const source = dest === f ? s : f
      this.setLink(this.links.rows, source, dest)
    },

    setColumnLink: function (f, s) {
      const dest = f < s ? f : s
      const source = dest === f ? s : f
      this.setLink(this.links.columns, source, dest)
    },

    mergeAllColumns: function () {
      let initial = -1
      for (let i = 0; i !== this.links.columns.length; ++i) {
        const list = this.links.columns[i]
        if (list.length === 0) {
          continue
        }
        if (initial < 0) {
          initial = i
        } else {
          this.setColumnLink(initial, i)
        }
      }
    },

    resetColLinks: function () {
      if (this.values == null) {
        this.links.columns = []
        return
      }

      const columns = this.values.columns
      const c = []
      for (let i = 0; i !== columns.length; ++i) {
        c.push([i])
      }
      this.links.columns = c
    },

    resetRowsLinks: function () {
      if (this.values == null) {
        this.links.rows = []
        return
      }

      const nbRows = this.values.rowKeys.size
      const r = []
      for (let i = 0; i !== nbRows; ++i) {
        r.push([i])
      }
      this.links.rows = r
    },

    resetLinks: function () {
      this.resetDisplayAllFieldValuesButton()
      this.resetColLinks()
      if (this.rowsLinkAvailable) {
        this.resetRowsLinks()
      }
    },

    toggleChart: function (i) {
      if (this.chart === i) {
        this.chart = null
      } else {
        this.chart = i
      }
    },

    mmChart: function (id) {
      const lv = this.linkedValues
      const toggleMed = {
        type: 'checkbox',
        style: 'bactADI',
        model: 'median',
        label: 'Médiane'
      }

      const toggleMean = {
        type: 'checkbox',
        style: 'neoInfADI',
        model: 'mean',
        label: 'Moyenne'
      }

      const toggleMeanp = {
        type: 'checkbox',
        style: 'reaExplaur',
        model: 'meanp',
        label: 'Moyenne pondérée',
        show: lv.rows.length >= 6
      }

      const toggleIndicator = {
        type: 'checkbox',
        style: 'cleanHandProx',
        model: 'indicator',
        label: 'Indicateur'
      }

      const toggleValue = {
        type: 'checkbox',
        style: 'reaInfADI',
        model: 'value',
        label: 'Valeur'
      }

      const toggleCurve = {
        type: 'checkbox',
        style: 'observa4',
        model: 'curve',
        label: 'Courbe'
      }

      const toggleBoxPlot = {
        type: 'checkbox',
        style: 'results',
        model: 'boxplot',
        label: 'Moustaches'
      }

      const togglePoints = {
        type: 'checkbox',
        style: 'invalid',
        model: 'points',
        label: 'Points'
      }

      const toggleOutliers = {
        type: 'checkbox',
        style: 'expADI',
        model: 'outliers',
        label: 'Outliers'
      }

      const toggleLimit = {
        type: 'checkbox',
        style: 'danger',
        model: 'limit',
        label: 'Limite',
        show: this.actions[toggleIndicator.model]
      }

      const limitInput = {
        type: 'number',
        model: 'limitValue',
        label: `Cible de l'indicateur`,
        show: this.actions[toggleIndicator.model]
      }

      const meanColor = ctool('#17dab5').fade(0.4).hsl().string()
      const meanDset = {
        type: 'line',
        data: [],
        values: [],
        borderColor: meanColor,
        backgroundColor: meanColor,
        tension: 0,
        fill: false,
        order: 1,
        hidden: !this.actions[toggleMean.model],
        showMean: true,
        showLine: this.actions[toggleCurve.model]
      }

      const meanpColor = ctool('#a888c9').fade(0.1).hsl().string()
      const meanpDset = {
        type: 'line',
        data: [],
        values: [],
        borderColor: meanpColor,
        backgroundColor: meanpColor,
        tension: 0,
        fill: false,
        order: 1,
        hidden: !this.actions[toggleMeanp.model],
        showMeanp: true,
        showLine: this.actions[toggleCurve.model]
      }

      const medColor = ctool(results.qualities[0].color).fade(0.4).hsl().string()
      const medDset = {
        type: 'line',
        data: [],
        values: [],
        borderColor: medColor,
        backgroundColor: medColor,
        tension: 0,
        fill: false,
        order: 2,
        hidden: !this.actions[toggleMed.model],
        showIncidence: true,
        showLine: this.actions[toggleCurve.model],
        showQuality: true
      }

      const showBoxPlot = this.actions[toggleBoxPlot.model]
      const bpDset = {
        type: 'boxplot',
        order: 3,
        data: [],
        values: [],
        stats: [],
        backgroundColor: [],
        borderColor: [],
        medianColor: [],
        showBoxPlot: showBoxPlot,
        showOutliers: this.actions[toggleOutliers.model],
        showIndicator: this.actions[toggleIndicator.model]
      }

      const labels = new Labels()

      const cols = lv.columns
      const meanps = (lv.rows.length >= 6 ? lv.rows[6] : null)
      const values = lv.rows[lv.rows.length - 1]
      const limit = this.getLimit
      const c = ctool('#fc8375').fade(0.5)
      for (let i = 0; i !== cols.length; ++i) {
        const col = cols[i].key
        const value = values.values[col]
        const stat = lv.data[col].stat
        const med = stat.median
        const mean = stat.mean
        const meanp = meanps ? meanps.values[col] : null

        if (med == null || mean == null || value == null) {
          bpDset.data.push([])
          bpDset.values.push([])
          bpDset.backgroundColor.push(null)
          bpDset.borderColor.push(null)
          meanDset.data.push(null)
          medDset.data.push(null)
          meanDset.values.push(null)
          medDset.values.push(null)

          if (meanps != null) {
            meanpDset.data.push(null)
            meanpDset.values.push(null)
          }
          continue
        }

        let t = 0
        for (let i = 0; i !== value.values.length; ++i) {
          const inc = value.values[i]
          if (limit !== null && inc <= limit) {
            ++t
          }
        }

        const v = (t / value.values.length) * 100

        const plotValue = {
          incidence: med,
          mean: mean,
          quality: stat.quality
        }

        meanDset.values.push({ value: plotValue, median: plotValue.mean })
        medDset.values.push({ value: plotValue, median: med })

        if (meanps) {
          plotValue.meanp = meanp
          meanpDset.values.push({ value: plotValue, median: plotValue.meanp })
        }

        if (limit != null) {
          plotValue.indicator = {
            value: v,
            valid: t
          }
        }

        incidenceBoxPlot.add(
          bpDset,
          labels,
          plotValue,
          {
            pop: value.values,
            n: value.values.length
          },
          c,
          cols[i].label,
          false,
          stat
        )

        meanDset.data.push(mean)
        medDset.data.push(med)
        if (meanps) {
          meanpDset.data.push(meanp)
        }
      }

      const fi = this.fieldInfo
      const title = {
        main: fi.field.t,
        sub: fi.subtitle
      }

      const paddingTop =
          this.actions[toggleValue.model] &&
          limitInput.show &&
          limit != null ? 80 : 50

      const showPoints = this.actions[togglePoints.model]
      const actions = [
        toggleMed,
        toggleOutliers,
        toggleMean,
        toggleMeanp,
        toggleIndicator,
        limitInput,
        toggleLimit,
        toggleValue,
        toggleCurve,
        togglePoints,
        toggleBoxPlot
      ]

      const dsets = [bpDset, meanDset, medDset]
      if (meanps) {
        dsets.push(meanpDset)
      }

      const minimizeDecimalScale = fi.field.minimizeDecimalScale
      const decimalScalePlugins = []
      if (minimizeDecimalScale != null && minimizeDecimalScale) {
        decimalScalePlugins.push(options.plotDecimalScale())
      }

      const result = {
        id: id,
        type: 'boxplot',
        icon: 'boxplot',
        refresh: limit,
        exportConfig: [
          ...options.incidenceTitles(title),
          options.titleFontSize(16),
          options.subtitleFontSize(14),
          options.axesFontSize,
          options.datalabelFontSize(),
          options.maxYValue(`Valeur maximale`),
          options.minYValue(`Valeur minimale`),
          options.toggleQ1Q3(),
          ...decimalScalePlugins,
          options.toggleStandardDeviation(),
          options.toggleIndicatorColors(),
          options.toggleQuality(dsets[2])
        ],
        plugins: [
          incidenceBoxPlot.plugins.drawTitle(),
          incidenceBoxPlot.plugins.outlierLimit(),
          incidenceBoxPlot.plugins.indicatorLimit(limit)
        ],
        config: {
          options: incidenceBoxPlot.options(
            {
              qualities: results.qualities,
              padding: {
                top: paddingTop,
                left: 10,
                right: 30
              },
              titleFontSize: 16,
              subtitleFontSize: 14,
              boxandwhiskers: showPoints ? null : {
                itemRadius: 0,
                outlierRadius: 0
              },
              showOutlierLimit: this.actions[toggleOutliers.model],
              showIndicatorLimit: this.actions[toggleLimit.model],
              showValues: this.actions[toggleValue.model]
            },
            title,
            c,
            labels,
            minimizeDecimalScale
          ),
          data: {
            datasets: dsets
          }
        },
        actions: actions
      }
      return result
    }
  },

  mounted: function () {
    this.resetLinks()
  }
}
</script>
<style scoped lang="less">
@import '~variables';

h3 {
  margin-top: 0rem;
}

.icon-container {
  a {
    background-color: @base;
    border-radius: 0.8rem;
    width: 6rem;

    display: inline-block;
    margin-right: 1rem;

    &.selected {
      background-color: @brand-warning;
    }

    &:hover {
      background-color: darken(@brand-warning, 20%);
    }

  }
}

</style>
<style lang="less">
@import '~sui/all';

.action-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.test-stat-result {
  display: flex;
  flex-direction: column;

  table {
    .table;
    .table-striped;
    .table-responsive;
    .table-fixed-first-row;
  }

  .conclusion {
    font-weight: bold;
    font-size: 100%;
  }
}

.test-stat-detail {
  .test-stat-result {
    .conclusion {
      margin-bottom: 1rem;
    }
  }
}

.test-stat-result-ok {
  .conclusion {
    color: @brand-success;
  }
}

.test-stat-result-nok {
  .conclusion {
    color: @brand-warning;
  }
}

.table-fixed-first-row {
  th {
    text-align: center;
  }

  text-align: center;
  table-layout: fixed;
  max-width: 1000px;
  tr th:first-child, tr td:first-child {
    width: 5rem;
  }
}

.btn-toolbar > .c-checkbox {
  margin-left: 5px;
}
</style>
